import React, {useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import {Button, InputNumber, message, Select} from 'antd'
import {FaFileExport, FaFileImport} from 'react-icons/fa6'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {callApi} from 'modules/asyncCache/callApi'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems} from 'views/Shared'
import {
    course_class_getGrades_Api,
    courseImportExport_applyImport_Api,
    courseImportExport_exportTemplate_Api,
    courseImportExport_previewImport_Api,
} from 'apis/course'
import {
    ClassSubjectPicker,
    CourseClassPicker,
    InfoBanner,
    ModeToggle,
    PreviewRowCard,
    RequiredMark,
} from './CourseImportExportShared'
import {GRADE_SCORE_TYPE_OPTIONS, SESSION_IMPORT_MODES} from './course_import_export_config'

const GRADE_MODE_OPTIONS = [
    {value: SESSION_IMPORT_MODES.INSERT, label: 'grade mode new column'},
    {value: SESSION_IMPORT_MODES.UPSERT, label: 'grade mode update column'},
]

// Chọn cột điểm ĐÃ CÓ - dùng chung Xuất & Nhập, mirror ĐÚNG ExistingSessionPicker (AttendancePanels.js).
const ExistingGradePicker = ({courseClass, subject, value, onChange, t}) => {
    const {response, isLoading, handleAsyncAction: fetchGrades} = useAsyncAction({
        apiInfo: course_class_getGrades_Api,
    })
    const ready = !!courseClass?.id && !!subject?.id
    useEffect(() => {
        if (!ready) return
        fetchGrades({subject_id: subject.id, sort_by: 'date desc'}, {id: courseClass.id})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, courseClass?.id, subject?.id])
    const items = ready ? getResponseItems(response) : []

    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('grade column to update')} <RequiredMark/></span>
            {ready ? (
                <Select
                    size="large" allowClear loading={isLoading}
                    placeholder={t('select grade column placeholder')}
                    value={value?.id}
                    onChange={(id) => onChange(items.find((i) => i.id === id) || null)}
                    options={items.map((i) => ({value: i.id, label: i.title}))}
                    notFoundContent={<span className="text-xs text-color-300 italic">{t('no grade column found')}</span>}
                />
            ) : (
                <div className="h-[46px] flex items-center px-3 rounded-lg border border-dashed border-color-200 text-xs text-color-300 italic">
                    {t('select subject first hint')}
                </div>
            )}
        </div>
    )
}

// ─── Tab Xuất ───────────────────────────────────────────────────────────────────────────────────
export const GradeExportPanel = ({entity, owner_id, owner_type, t}) => {
    const [mode, setMode] = useState(SESSION_IMPORT_MODES.INSERT)
    const [course, setCourse] = useState(null)
    const [courseClass, setCourseClass] = useState(null)
    const [subject, setSubject] = useState(null)

    // mode=insert - field định danh cột điểm MỚI.
    const [gradeTitle, setGradeTitle] = useState('')
    const [scoreType, setScoreType] = useState('NUMBER')
    const [scoreWeight, setScoreWeight] = useState(100)

    // mode=upsert - cột điểm đã chọn để cập nhật.
    const [selectedGrade, setSelectedGrade] = useState(null)

    const hasCourseClassSubject = !!course?.id && !!courseClass?.id && !!subject?.id

    const {handleAsyncAction: doExport, loading: exporting} = useAsyncAction({
        apiInfo: courseImportExport_exportTemplate_Api,
        onSuccess: (result, {response}) => {
            const url = _.get(response, 'data.url')
            if (url) window.open(url, '_blank')
            message.success(t('import export template exported'))
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    const canExport = hasCourseClassSubject && (
        mode === SESSION_IMPORT_MODES.INSERT
            ? !!gradeTitle && scoreWeight > 0
            : !!selectedGrade?.id
    )

    const handleExport = () => {
        const baseParams = {course_id: course.id, course_class_id: courseClass.id, subject_id: subject.id, mode}
        const params = mode === SESSION_IMPORT_MODES.INSERT
            ? {...baseParams, grade_title: gradeTitle, score_type: scoreType, score_weight: scoreWeight}
            : {...baseParams, grade_id: selectedGrade.id}
        doExport(params, {type: owner_type, id: owner_id, key: entity.key})
    }

    return (
        <div className="flex flex-col gap-3">
            <InfoBanner>{t('course grade export hint')}</InfoBanner>

            <CourseClassPicker owner_id={owner_id} owner_type={owner_type} course={course} setCourse={setCourse} courseClass={courseClass} setCourseClass={setCourseClass} t={t}/>
            <ClassSubjectPicker courseClass={courseClass} subject={subject} setSubject={setSubject} t={t}/>

            <ModeToggle value={mode} onChange={setMode} options={GRADE_MODE_OPTIONS} t={t}/>

            {mode === SESSION_IMPORT_MODES.INSERT ? (
                <>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('grade column title')} <RequiredMark/></span>
                        <input
                            className="h-[46px] px-3 rounded-lg border border-color-200 text-sm background focus:border-amber-400 focus:outline-none"
                            placeholder={t('grade column title placeholder')}
                            value={gradeTitle}
                            onChange={(e) => setGradeTitle(e.target.value)}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('grade score type')} <RequiredMark/></span>
                            <Select
                                size="large" value={scoreType} onChange={setScoreType}
                                options={GRADE_SCORE_TYPE_OPTIONS.map((o) => ({value: o.value, label: t(o.label)}))}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('grade score weight')} <RequiredMark/></span>
                            <InputNumber size="large" className="w-full" min={1} max={100} value={scoreWeight} onChange={setScoreWeight}/>
                        </div>
                    </div>
                </>
            ) : (
                <ExistingGradePicker courseClass={courseClass} subject={subject} value={selectedGrade} onChange={setSelectedGrade} t={t}/>
            )}

            <Button
                type="primary" size="large" icon={<FaFileExport size={13}/>} loading={exporting}
                disabled={!canExport}
                onClick={handleExport}>
                {t(mode === SESSION_IMPORT_MODES.INSERT ? 'export blank template' : 'export selected template')}
            </Button>
        </div>
    )
}

// ─── Tab Nhập ───────────────────────────────────────────────────────────────────────────────────
const GradePreviewRow = ({row, t}) => (
    <PreviewRowCard row={row} title={row.student_name} t={t}>
        <span className="text-xs text-color-400">
            {row.grade_title}
            {row.score ? ` · ${t('score')}: ${row.score}` : ''}
            {row.score_1 ? ` · ${t('retake 1 short')}: ${row.score_1}` : ''}
            {row.score_2 ? ` · ${t('retake 2 short')}: ${row.score_2}` : ''}
        </span>
        {row.result_status_override !== null && row.result_status_override !== undefined && (
            <span className={classNames('text-xs font-medium block', row.result_status_override === 1 ? 'text-emerald-600' : 'text-rose-600')}>
                {row.result_status_override === 1 ? t('manual result passed') : t('manual result failed')}
            </span>
        )}
    </PreviewRowCard>
)

export const GradeImportPanel = ({entity, owner_id, owner_type, t}) => {
    const inputRef = useRef(null)
    const [uploading, setUploading] = useState(false)
    const [preview, setPreview] = useState(null)
    const [mode, setMode] = useState(SESSION_IMPORT_MODES.INSERT)
    const [course, setCourse] = useState(null)
    const [courseClass, setCourseClass] = useState(null)
    const [subject, setSubject] = useState(null)
    const [selectedGrade, setSelectedGrade] = useState(null)

    const hasCourseClassSubject = !!course?.id && !!courseClass?.id && !!subject?.id
    const ready = mode === SESSION_IMPORT_MODES.INSERT ? hasCourseClassSubject : (hasCourseClassSubject && !!selectedGrade?.id)

    const handleFile = (file) => {
        if (!file || uploading || !ready) return
        setUploading(true)
        callApi({
            apiInfo: courseImportExport_previewImport_Api,
            query: bindQueryParams([{type: owner_type}, {id: owner_id}, {key: entity.key}]),
            values: {
                file, course_id: course.id, course_class_id: courseClass.id, subject_id: subject.id,
                grade_id: mode === SESSION_IMPORT_MODES.UPSERT ? selectedGrade.id : undefined,
            },
        }).then((result) => {
            setUploading(false)
            if (inputRef.current) inputRef.current.value = ''
            if (!result) return
            if (result.error) {
                message.error(_.get(result, 'errorMessages.error') || t('error occurred'))
                return
            }
            setPreview(result.result)
        })
    }

    const {handleAsyncAction: doApply, loading: applying} = useAsyncAction({
        apiInfo: courseImportExport_applyImport_Api,
        onSuccess: (result, {response}) => {
            const data = _.get(response, 'data')
            message.success(`${t('import export applied')}: ${data?.applied ?? 0}${data?.failed ? ` (${t('error')}: ${data.failed})` : ''}`)
            setPreview(null)
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    if (!preview) {
        return (
            <div className="flex flex-col gap-3">
                <InfoBanner>{t('import export upload hint')}</InfoBanner>
                <CourseClassPicker owner_id={owner_id} owner_type={owner_type} course={course} setCourse={setCourse} courseClass={courseClass} setCourseClass={setCourseClass} t={t}/>
                <ClassSubjectPicker courseClass={courseClass} subject={subject} setSubject={setSubject} t={t}/>
                <ModeToggle value={mode} onChange={setMode} options={GRADE_MODE_OPTIONS} t={t}/>
                {mode === SESSION_IMPORT_MODES.UPSERT && (
                    <ExistingGradePicker courseClass={courseClass} subject={subject} value={selectedGrade} onChange={setSelectedGrade} t={t}/>
                )}
                <label className={classNames(
                    'p-8 flex flex-col items-center gap-2 rounded-xl border-2 border-dashed transition-all',
                    ready ? 'border-amber-300 hover:bg-amber-50 cursor-pointer' : 'border-color-200 opacity-50 cursor-not-allowed')}>
                    <FaFileImport size={26} className="text-amber-600"/>
                    <span className="text-sm font-semibold text-color-000">
                        {!ready ? t('select course first hint') : (uploading ? t('uploading') : t('choose file to upload'))}
                    </span>
                    <input
                        ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden"
                        disabled={uploading || !ready}
                        onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                </label>
            </div>
        )
    }

    const validCount = preview.rows.filter((r) => r.status === 1).length

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-color-000">
                    {t('import export summary')}: {preview.valid}/{preview.total}
                </span>
                <button type="button" onClick={() => setPreview(null)} className="text-xs text-amber-700 underline cursor-pointer flex-shrink-0">
                    {t('choose another file')}
                </button>
            </div>

            <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
                {preview.rows.map((row) => <GradePreviewRow key={row.id} row={row} t={t}/>)}
            </div>

            <Button
                type="primary" size="large" loading={applying} disabled={!validCount}
                onClick={() => doApply({batch_id: preview.batch_id}, {type: owner_type, id: owner_id, key: entity.key})}>
                {t('apply import')} ({validCount})
            </Button>
        </div>
    )
}
